import { useState, useEffect, useMemo, useRef } from 'react'
import { Table, Button, Badge, Modal, Form, Row, Col, Card, Tab, Tabs, OverlayTrigger, Tooltip, ButtonGroup, Dropdown } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { timeConditionLayersApi, timeConditionsApi, recordingsApi, holidaysApi } from '../services/api'
import { DestinationSelector } from '../components/common'
import { useConfirm } from '../components/common/ConfirmDialog'
import { useAuthStore } from '../store/authStore'

// ============================================
// Types
// ============================================

interface TimeEntry {
  id?: string
  weekdays: string
  start_time: string
  end_time: string
  start_date?: string | null
  end_date?: string | null
  day_of_month_start?: number | null
  day_of_month_end?: number | null
  month_start?: number | null
  month_end?: number | null
  comment?: string
  holiday_definition_id?: string | null
  generated_for_year?: number | null
}

interface TimeEntryForm {
  id?: string
  weekdays: string[]
  start_time: string
  end_time: string
  start_date: string
  end_date: string
  date_mode: 'always' | 'range' | 'yearly'
  month_start: number
  day_of_month_start: number
  month_end: number
  day_of_month_end: number
  comment: string
  holiday_definition_id?: string | null
  generated_for_year?: number | null
}

interface Recording {
  id: string
  name: string
  filename: string
}

interface TimeCondition {
  id: string
  name: string
  description: string
  match_action_type: string
  match_action_target: string
  match_intro_recording_id: string | null
  nomatch_action_type: string | null
  nomatch_action_target: string | null
  nomatch_intro_recording_id: string | null
  priority: number
  is_active: boolean
  time_entries: TimeEntry[]
  layer_id: string
}

interface TimeConditionLayer {
  id: string
  name: string
  description: string
  nomatch_action_type: string
  nomatch_action_target: string
  nomatch_intro_recording_id: string | null
  is_active: boolean
  time_conditions: TimeCondition[]
}

// TC form (for single TC modal)
interface TCFormData {
  name: string
  description: string
  match_action_type: string
  match_action_target: string
  match_intro_recording_id: string
  nomatch_action_type: string
  nomatch_action_target: string
  nomatch_intro_recording_id: string
  priority: number
  is_active: boolean
  time_entries: TimeEntryForm[]
}

// Holiday types
interface Canton { code: string; name: string }
interface HolidayDefinition {
  id: string; name: string; holiday_type: string
  fixed_month?: number; fixed_day?: number; easter_offset?: number
  custom_date?: string; custom_recurs_yearly: boolean
  is_system: boolean; is_enabled: boolean
}
interface HolidayConfig {
  id: string; tenant_id: string; canton: string | null
  subdivision_code: string | null
  is_active: boolean; auto_regenerate: boolean; holiday_definitions: HolidayDefinition[]
}
interface HolidayPreview { name: string; date: string; weekday: string; is_system: boolean }
interface Subdivision { code: string; name: string; parent_code: string | null; depth: number }

// Layer form (for layer settings modal)
interface LayerFormData {
  name: string
  description: string
  nomatch_action_type: string
  nomatch_action_target: string
  nomatch_intro_recording_id: string
  is_active: boolean
}

// ============================================
// Constants & Helpers
// ============================================

const weekdays = [
  { value: 'mon', label: 'Mo', fullLabel: 'Montag', index: 1 },
  { value: 'tue', label: 'Di', fullLabel: 'Dienstag', index: 2 },
  { value: 'wed', label: 'Mi', fullLabel: 'Mittwoch', index: 3 },
  { value: 'thu', label: 'Do', fullLabel: 'Donnerstag', index: 4 },
  { value: 'fri', label: 'Fr', fullLabel: 'Freitag', index: 5 },
  { value: 'sat', label: 'Sa', fullLabel: 'Samstag', index: 6 },
  { value: 'sun', label: 'So', fullLabel: 'Sonntag', index: 0 }
]

const months = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
]

const TC_ALLOWED_TYPES = [
  'extension', 'ring_group', 'queue', 'ivr', 'time_condition',
  'conference', 'voicemail', 'parking', 'night_mode', 'announcement', 'recording',
  'external_dest', 'external', 'hangup',
]

const COLORS = [
  '#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#00BCD4',
  '#E91E63', '#3F51B5', '#009688', '#FFC107', '#795548'
]

const weekdaysStringToArray = (str: string): string[] => {
  if (!str) return []
  return str.split(',').map(s => s.trim()).filter(Boolean)
}
const weekdaysArrayToString = (arr: string[]): string => arr.join(',')

const timeEntriesToForm = (entries: TimeEntry[]): TimeEntryForm[] =>
  (entries || []).map(e => ({
    id: e.id,
    weekdays: weekdaysStringToArray(e.weekdays),
    start_time: e.start_time || '08:00',
    end_time: e.end_time || '17:00',
    start_date: e.start_date || '',
    end_date: e.end_date || '',
    date_mode: (e.month_start || e.day_of_month_start) ? 'yearly' : (e.start_date || e.end_date) ? 'range' : 'always',
    month_start: e.month_start || 1,
    day_of_month_start: e.day_of_month_start || 1,
    month_end: e.month_end || e.month_start || 1,
    day_of_month_end: e.day_of_month_end || e.day_of_month_start || 1,
    comment: e.comment || '',
    holiday_definition_id: e.holiday_definition_id || null,
    generated_for_year: e.generated_for_year || null
  }))

// Only non-holiday entries get sent to the backend - backend preserves holiday entries on update
const formToTimeEntries = (entries: TimeEntryForm[]): Partial<TimeEntry>[] =>
  entries.filter(e => !e.holiday_definition_id).map(e => ({
    id: e.id,
    weekdays: e.date_mode === 'yearly' ? undefined : weekdaysArrayToString(e.weekdays),
    start_time: e.start_time,
    end_time: e.end_time,
    start_date: e.date_mode === 'range' && e.start_date ? e.start_date : undefined,
    end_date: e.date_mode === 'range' && e.end_date ? e.end_date : undefined,
    month_start: e.date_mode === 'yearly' ? e.month_start : undefined,
    day_of_month_start: e.date_mode === 'yearly' ? e.day_of_month_start : undefined,
    month_end: e.date_mode === 'yearly' ? e.month_end : undefined,
    day_of_month_end: e.date_mode === 'yearly' ? e.day_of_month_end : undefined,
    comment: e.comment || undefined
  }))

const emptyTCForm: TCFormData = {
  name: '', description: '',
  match_action_type: '', match_action_target: '', match_intro_recording_id: '',
  nomatch_action_type: '', nomatch_action_target: '', nomatch_intro_recording_id: '',
  priority: 10, is_active: true, time_entries: []
}

const emptyLayerForm: LayerFormData = {
  name: '', description: '',
  nomatch_action_type: '', nomatch_action_target: '', nomatch_intro_recording_id: '',
  is_active: true
}

const formatWeekdaysBadge = (wd: string) => {
  if (!wd) return 'Alle Tage'
  const days = weekdaysStringToArray(wd)
  if (days.length === 7) return 'Alle Tage'
  if (days.length === 5 && ['mon','tue','wed','thu','fri'].every(d => days.includes(d))) return 'Mo-Fr'
  if (days.length === 2 && ['sat','sun'].every(d => days.includes(d))) return 'Sa-So'
  return days.map(d => weekdays.find(w => w.value === d)?.label || d).join(', ')
}

// Calendar helpers
const getMonday = (d: Date): Date => {
  const date = new Date(d); const day = date.getDay()
  return new Date(date.setDate(date.getDate() - day + (day === 0 ? -6 : 1)))
}
const formatDate = (d: Date) => `${d.getDate().toString().padStart(2,'0')}.${(d.getMonth()+1).toString().padStart(2,'0')}.${d.getFullYear()}`
const formatDateISO = (d: Date) => `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}`

// Parse "HH:MM" or "HH:MM:SS" to minutes since midnight
const timeToMinutes = (t: string | null | undefined): number => {
  if (!t) return 0
  const parts = t.split(':')
  return parseInt(parts[0]) * 60 + parseInt(parts[1] || '0')
}

// Check if a yearly entry matches a date (month/day range)
const checkYearlyMatch = (entry: TimeEntry, date: Date): boolean => {
  const m = date.getMonth() + 1, d = date.getDate()
  const ms = entry.month_start || 0, ds = entry.day_of_month_start || 0
  const me = entry.month_end || ms, de = entry.day_of_month_end || ds
  if (!ms || !ds) return false
  const val = m * 100 + d, start = ms * 100 + ds, end = me * 100 + de
  return val >= start && val <= end
}

// Check if weekday/date conditions match for an entry
const checkEntryDateMatch = (entry: TimeEntry, date: Date, dateStr: string, dayValue: string): boolean => {
  // Yearly entries: check month/day, ignore weekdays
  if (entry.month_start && entry.day_of_month_start) {
    return checkYearlyMatch(entry, date)
  }
  // Date range entries
  if (entry.start_date && dateStr < entry.start_date) return false
  if (entry.end_date && dateStr > entry.end_date) return false
  // Weekday check: empty weekdays = all days (for holiday/date entries)
  const wd = weekdaysStringToArray(entry.weekdays)
  if (wd.length > 0 && !wd.includes(dayValue)) return false
  return true
}

// Check if a TC applies on a given date. minuteOfDay is optional (0-1439).
const checkConditionApplies = (tc: TimeCondition, date: Date, minuteOfDay?: number): boolean => {
  const dayValue = weekdays.find(w => w.index === date.getDay())?.value || ''
  const dateStr = formatDateISO(date)
  for (const entry of tc.time_entries || []) {
    if (!checkEntryDateMatch(entry, date, dateStr, dayValue)) continue
    if (minuteOfDay !== undefined) {
      const s = entry.start_time ? timeToMinutes(entry.start_time) : 0
      const e = entry.end_time ? timeToMinutes(entry.end_time) : 1439
      if (minuteOfDay < s || minuteOfDay >= e) continue
    }
    return true
  }
  return false
}

// Get the coverage ratio (0-1) of a TC within a given hour on a date
const getHourCoverage = (tc: TimeCondition, date: Date, hour: number): { start: number; end: number } | null => {
  const dayValue = weekdays.find(w => w.index === date.getDay())?.value || ''
  const dateStr = formatDateISO(date)
  const hourStart = hour * 60
  const hourEnd = (hour + 1) * 60
  for (const entry of tc.time_entries || []) {
    if (!checkEntryDateMatch(entry, date, dateStr, dayValue)) continue
    const s = entry.start_time ? timeToMinutes(entry.start_time) : 0
    const e = entry.end_time ? timeToMinutes(entry.end_time) : 1440
    // Check overlap with this hour
    const overlapStart = Math.max(s, hourStart)
    const overlapEnd = Math.min(e, hourEnd)
    if (overlapStart < overlapEnd) {
      return { start: (overlapStart - hourStart) / 60, end: (overlapEnd - hourStart) / 60 }
    }
  }
  return null
}

// ============================================
// Component
// ============================================

export default function TimeConditions() {
  const confirm = useConfirm()
  const { selectedTenant } = useAuthStore()

  const [layers, setLayers] = useState<TimeConditionLayer[]>([])
  const [recordings, setRecordings] = useState<Recording[]>([])
  const [loading, setLoading] = useState(true)

  // Layer selection
  const [selectedLayerId, setSelectedLayerId] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState('')
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)

  // Layer modal (name, fallback etc)
  const [showLayerModal, setShowLayerModal] = useState(false)
  const [editingLayer, setEditingLayer] = useState<TimeConditionLayer | null>(null)
  const [layerForm, setLayerForm] = useState<LayerFormData>(emptyLayerForm)

  // TC modal (single TC edit)
  const [showTCModal, setShowTCModal] = useState(false)
  const [editingTC, setEditingTC] = useState<TimeCondition | null>(null)
  const [tcForm, setTCForm] = useState<TCFormData>(emptyTCForm)

  // Move modal
  const [showMoveModal, setShowMoveModal] = useState(false)
  const [movingTC, setMovingTC] = useState<TimeCondition | null>(null)
  const [showCloneModal, setShowCloneModal] = useState(false)
  const [cloningTC, setCloningTC] = useState<TimeCondition | null>(null)
  const [cloneTargetLayerId, setCloneTargetLayerId] = useState<string>('')
  const [moveTargetLayerId, setMoveTargetLayerId] = useState<string>('')

  // View state
  const [activeTab, setActiveTab] = useState<string>('list')
  const [calendarView, setCalendarView] = useState<'week' | 'month' | 'year'>('week')
  // Calendar cell-add flow:
  //   - User selects a TC by clicking its badge (or sets to null = create new TC)
  //   - User clicks empty cells; plain click → opens dialog with new time-entry
  //   - Shift+click → adds cell to a multi-selection without opening dialog
  const [selectedCalendarTcId, setSelectedCalendarTcId] = useState<string | null>(null)
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set())
  // Drag-selection: hold left mouse button + drag over cells to mark a range.
  // dragModeRef: 'shift' = additive (don't open dialog on release),
  //              'plain' = replace + finalize on release (open dialog).
  const dragModeRef = useRef<'plain' | 'shift' | null>(null)
  const dragCellsRef = useRef<Set<string>>(new Set())
  const [currentDate, setCurrentDate] = useState(new Date())

  // Holiday state
  const [cantons, setCantons] = useState<Canton[]>([])
  const [holidayConfig, setHolidayConfig] = useState<HolidayConfig | null>(null)
  const [holidayPreview, setHolidayPreview] = useState<HolidayPreview[]>([])
  const [previewYear, setPreviewYear] = useState(new Date().getFullYear())
  const [holidayLoading, setHolidayLoading] = useState(false)
  const [subdivisions, setSubdivisions] = useState<Subdivision[]>([])
  const [showCustomHolidayModal, setShowCustomHolidayModal] = useState(false)
  const [customHolidayForm, setCustomHolidayForm] = useState({ name: '', holiday_type: 'FIXED' as string, fixed_month: 1, fixed_day: 1, easter_offset: 0, custom_date: '', custom_recurs_yearly: true })
  const [, setGenerateTCId] = useState<string>('')

  useEffect(() => { loadLayers(); loadRecordings(); loadCantons() }, [])

  // Reload tenant-specific data when the selected tenant changes (super_admin tenant switch)
  useEffect(() => {
    // Reset holiday state so the next time the Feiertage tab is opened it reloads for the new tenant
    setHolidayConfig(null)
    setHolidayPreview([])
    setGenerateTCId('')
    loadLayers()
    loadRecordings()
    // If the Feiertage tab is currently active, immediately reload it for the new tenant
    if (activeTab === 'holidays') loadHolidayConfig()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant?.id])
  useEffect(() => { if (layers.length > 0 && !selectedLayerId) setSelectedLayerId(layers[0].id) }, [layers])

  const selectedLayer = useMemo(() => layers.find(l => l.id === selectedLayerId) || null, [layers, selectedLayerId])
  const sortedTCs = useMemo(() => {
    if (!selectedLayer) return []
    return [...(selectedLayer.time_conditions || [])].sort((a, b) => (a.priority || 10) - (b.priority || 10))
  }, [selectedLayer])
  const filteredLayers = useMemo(() => {
    if (!searchTerm) return layers
    const t = searchTerm.toLowerCase()
    return layers.filter(l => l.name.toLowerCase().includes(t) || (l.description || '').toLowerCase().includes(t))
  }, [layers, searchTerm])

  const loadLayers = async () => {
    try {
      const r = await timeConditionLayersApi.list()
      setLayers(r.data.items || [])
    } catch { toast.error('Fehler beim Laden') }
    finally { setLoading(false) }
  }

  const loadRecordings = async () => {
    try { const r = await recordingsApi.list(); setRecordings(r.data.items || []) }
    catch { /* ignore */ }
  }

  const loadCantons = async () => {
    try { const r = await holidaysApi.getCantons(); setCantons(r.data || []) }
    catch { /* ignore */ }
  }

  const loadHolidayConfig = async () => {
    try {
      const r = await holidaysApi.getConfig()
      setHolidayConfig(r.data)
      if (r.data?.canton) {
        loadHolidayPreview(previewYear)
        loadSubdivisions(r.data.canton)
      } else {
        setSubdivisions([])
      }
    } catch { /* ignore */ }
  }

  const loadSubdivisions = async (canton: string) => {
    try {
      const r = await holidaysApi.getSubdivisions(canton)
      setSubdivisions(r.data || [])
    } catch { setSubdivisions([]) }
  }

  const loadHolidayPreview = async (year: number) => {
    try { const r = await holidaysApi.preview(year); setHolidayPreview(r.data || []) }
    catch { /* ignore */ }
  }

  const handleToggleAutoRegenerate = async (enabled: boolean) => {
    setHolidayLoading(true)
    try {
      const r = await holidaysApi.updateConfig({ auto_regenerate: enabled })
      setHolidayConfig(r.data)
      toast.success(enabled ? 'Automatische jährliche Aktualisierung aktiviert' : 'Automatische Aktualisierung deaktiviert')
      if (enabled) loadLayers()
    } catch { toast.error('Fehler beim Speichern') }
    finally { setHolidayLoading(false) }
  }

  const handleCantonChange = async (canton: string) => {
    setHolidayLoading(true)
    try {
      const r = await holidaysApi.updateConfig({ canton: canton || null })
      setHolidayConfig(r.data)
      if (canton) {
        loadHolidayPreview(previewYear)
        loadSubdivisions(canton)
      } else {
        setHolidayPreview([])
        setSubdivisions([])
      }
      toast.success(canton ? `Kanton ${canton} ausgewählt` : 'Kanton entfernt')
    } catch { toast.error('Fehler beim Speichern') }
    finally { setHolidayLoading(false) }
  }

  const handleSubdivisionChange = async (code: string) => {
    setHolidayLoading(true)
    try {
      const r = await holidaysApi.updateConfig({ subdivision_code: code })
      setHolidayConfig(r.data)
      loadHolidayPreview(previewYear)
      toast.success(code ? 'Gemeinde gesetzt' : 'Kanton-Default wiederhergestellt')
    } catch { toast.error('Fehler beim Speichern') }
    finally { setHolidayLoading(false) }
  }

  const handleToggleHoliday = async (defn: HolidayDefinition) => {
    try {
      await holidaysApi.updateDefinition(defn.id, { is_enabled: !defn.is_enabled })
      loadHolidayConfig()
    } catch { toast.error('Fehler beim Aktualisieren') }
  }

  const handleAddCustomHoliday = async () => {
    const data: Record<string, unknown> = { name: customHolidayForm.name, holiday_type: customHolidayForm.holiday_type }
    if (customHolidayForm.holiday_type === 'FIXED') {
      data.fixed_month = customHolidayForm.fixed_month
      data.fixed_day = customHolidayForm.fixed_day
    } else if (customHolidayForm.holiday_type === 'EASTER_BASED') {
      data.easter_offset = customHolidayForm.easter_offset
    } else {
      data.custom_date = customHolidayForm.custom_date
      data.custom_recurs_yearly = customHolidayForm.custom_recurs_yearly
    }
    try {
      await holidaysApi.createDefinition(data)
      toast.success('Feiertag hinzugefügt')
      setShowCustomHolidayModal(false)
      loadHolidayConfig()
      loadHolidayPreview(previewYear)
    } catch { toast.error('Fehler beim Erstellen') }
  }

  const handleDeleteCustomHoliday = async (id: string) => {
    try {
      await holidaysApi.deleteDefinition(id)
      toast.success('Feiertag gelöscht')
      loadHolidayConfig()
      loadHolidayPreview(previewYear)
    } catch { toast.error('Fehler beim Löschen') }
  }

  const handleRemoveGeneratedHolidays = async (tcId: string) => {
    try {
      const r = await holidaysApi.removeGenerated(tcId)
      toast.success(`${r.data.removed} Einträge entfernt`)
      loadLayers()
    } catch { toast.error('Fehler beim Entfernen') }
  }

  // ============================================
  // Layer CRUD
  // ============================================

  const openCreateLayerModal = () => {
    setEditingLayer(null)
    setLayerForm(emptyLayerForm)
    setShowLayerModal(true)
  }

  const openEditLayerModal = (layer: TimeConditionLayer) => {
    setEditingLayer(layer)
    setLayerForm({
      name: layer.name, description: layer.description || '',
      nomatch_action_type: layer.nomatch_action_type || '',
      nomatch_action_target: layer.nomatch_action_target || '',
      nomatch_intro_recording_id: layer.nomatch_intro_recording_id || '',
      is_active: layer.is_active
    })
    setShowLayerModal(true)
  }

  const handleLayerSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!layerForm.nomatch_action_type) { toast.error('Bitte ein Fallback-Ziel auswählen'); return }
    const payload = {
      name: layerForm.name,
      description: layerForm.description || null,
      nomatch_action_type: layerForm.nomatch_action_type,
      nomatch_action_target: layerForm.nomatch_action_target || null,
      nomatch_intro_recording_id: layerForm.nomatch_intro_recording_id || null,
      is_active: layerForm.is_active
    }
    try {
      if (editingLayer) {
        await timeConditionLayersApi.update(editingLayer.id, payload as unknown as Record<string, unknown>)
        toast.success('Bedingung aktualisiert')
      } else {
        const resp = await timeConditionLayersApi.create(payload as unknown as Record<string, unknown>, selectedTenant?.id)
        toast.success('Bedingung erstellt')
        if (resp.data?.id) setSelectedLayerId(resp.data.id)
      }
      setShowLayerModal(false)
      loadLayers()
    } catch { toast.error('Fehler beim Speichern') }
  }

  const handleDeleteLayer = async (layer: TimeConditionLayer) => {
    const ok = await confirm({
      title: 'Bedingung löschen',
      message: `"${layer.name}" mit ${layer.time_conditions?.length || 0} Zeitbedingungen wirklich löschen?`,
      confirmText: 'Löschen', variant: 'danger'
    })
    if (!ok) return
    try {
      await timeConditionLayersApi.delete(layer.id)
      toast.success('Bedingung gelöscht')
      if (selectedLayerId === layer.id) setSelectedLayerId('')
      loadLayers()
    } catch { toast.error('Fehler beim Löschen') }
  }

  // ============================================
  // TC CRUD
  // ============================================

  const openCreateTCModal = () => {
    setEditingTC(null)
    const nextPrio = sortedTCs.length > 0 ? Math.max(...sortedTCs.map(t => t.priority || 10)) + 10 : 10
    setTCForm({ ...emptyTCForm, priority: nextPrio })
    setShowTCModal(true)
  }

  const openEditTCModal = (tc: TimeCondition) => {
    setEditingTC(tc)
    setTCForm({
      name: tc.name, description: tc.description || '',
      match_action_type: tc.match_action_type || '', match_action_target: tc.match_action_target || '',
      match_intro_recording_id: tc.match_intro_recording_id || '',
      nomatch_action_type: tc.nomatch_action_type || '', nomatch_action_target: tc.nomatch_action_target || '',
      nomatch_intro_recording_id: tc.nomatch_intro_recording_id || '',
      priority: tc.priority || 10, is_active: tc.is_active,
      time_entries: timeEntriesToForm(tc.time_entries)
    })
    setShowTCModal(true)
  }

  const openCloneModal = (tc: TimeCondition) => {
    setCloningTC(tc)
    // Default-Ziel: die aktuelle Layer der TC (= eigene Bedingung)
    setCloneTargetLayerId(selectedLayerId || '')
    setShowCloneModal(true)
  }

  const handleCloneTC = async () => {
    if (!cloningTC || !cloneTargetLayerId) return
    // Priorität: in der Ziel-Layer das Maximum + 10 nehmen
    const targetLayer = layers.find(l => l.id === cloneTargetLayerId)
    const targetTCs = targetLayer?.time_conditions || []
    const nextPrio = targetTCs.length > 0
      ? Math.max(...targetTCs.map(t => t.priority || 10)) + 10
      : 10
    const payload = {
      name: cloningTC.name + ' (Kopie)',
      description: cloningTC.description || null,
      match_action_type: cloningTC.match_action_type, match_action_target: cloningTC.match_action_target || null,
      match_intro_recording_id: cloningTC.match_intro_recording_id || null,
      nomatch_action_type: cloningTC.nomatch_action_type || null, nomatch_action_target: cloningTC.nomatch_action_target || null,
      nomatch_intro_recording_id: cloningTC.nomatch_intro_recording_id || null,
      priority: nextPrio,
      is_active: cloningTC.is_active,
      time_entries: (cloningTC.time_entries || []).map(te => ({
        weekdays: te.weekdays, start_time: te.start_time, end_time: te.end_time,
        start_date: te.start_date, end_date: te.end_date, comment: te.comment
      }))
    }
    try {
      await timeConditionsApi.create(payload, cloneTargetLayerId)
      toast.success('Zeitbedingung geklont')
      setShowCloneModal(false)
      setCloningTC(null)
      loadLayers()
    } catch { toast.error('Fehler beim Klonen') }
  }

  const handleDeleteTC = async (tc: TimeCondition) => {
    const ok = await confirm({
      title: 'Zeitbedingung löschen', message: `"${tc.name}" wirklich löschen?`,
      confirmText: 'Löschen', variant: 'danger'
    })
    if (!ok) return
    try {
      await timeConditionsApi.delete(tc.id)
      toast.success('Zeitbedingung gelöscht')
      loadLayers()
    } catch { toast.error('Fehler beim Löschen') }
  }

  const openMoveModal = (tc: TimeCondition) => {
    setMovingTC(tc)
    setMoveTargetLayerId('')
    setShowMoveModal(true)
  }

  const handleMoveTC = async () => {
    if (!movingTC || !moveTargetLayerId) return
    try {
      await timeConditionsApi.move(movingTC.id, moveTargetLayerId)
      toast.success('Zeitbedingung verschoben')
      setShowMoveModal(false)
      loadLayers()
    } catch { toast.error('Fehler beim Verschieben') }
  }

  const handleTCSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedLayer) return
    if (!tcForm.match_action_type) { toast.error('Bitte ein Match-Ziel auswählen'); return }

    const hasHolidays = tcForm.time_entries.some(e => e.holiday_definition_id)
    const manualEntries = formToTimeEntries(tcForm.time_entries)

    // At least one entry (manual or holiday) must exist
    if (manualEntries.length === 0 && !hasHolidays) {
      toast.error('Mindestens ein Zeitfenster erforderlich'); return
    }

    const payload: Record<string, unknown> = {
      name: tcForm.name, description: tcForm.description || null,
      match_action_type: tcForm.match_action_type, match_action_target: tcForm.match_action_target || null,
      match_intro_recording_id: tcForm.match_intro_recording_id || null,
      nomatch_action_type: tcForm.nomatch_action_type || null,
      nomatch_action_target: tcForm.nomatch_action_target || null,
      nomatch_intro_recording_id: tcForm.nomatch_intro_recording_id || null,
      priority: tcForm.priority, is_active: tcForm.is_active,
    }

    // Always send manual entries (may be empty if user kept only holidays).
    // Backend preserves holiday-generated entries separately.
    payload.time_entries = manualEntries

    try {
      if (editingTC) {
        await timeConditionsApi.update(editingTC.id, payload)
        toast.success('Zeitbedingung aktualisiert')
      } else {
        await timeConditionsApi.create(payload, selectedLayer.id)
        toast.success('Zeitbedingung erstellt')
      }
      setShowTCModal(false)
      loadLayers()
    } catch { toast.error('Fehler beim Speichern') }
  }

  // ============================================
  // Time entry helpers for TC form
  // ============================================

  const addTimeEntry = () => {
    setTCForm({
      ...tcForm, time_entries: [...tcForm.time_entries, {
        weekdays: ['mon','tue','wed','thu','fri'], start_time: '08:00', end_time: '17:00',
        start_date: '', end_date: '', date_mode: 'always',
        month_start: 1, day_of_month_start: 1, month_end: 1, day_of_month_end: 1,
        comment: ''
      }]
    })
  }
  const updateTimeEntry = (i: number, field: string, value: unknown) => {
    const entries = [...tcForm.time_entries]
    entries[i] = { ...entries[i], [field]: value }
    setTCForm({ ...tcForm, time_entries: entries })
  }
  const removeTimeEntry = (i: number) => {
    setTCForm({ ...tcForm, time_entries: tcForm.time_entries.filter((_, idx) => idx !== i) })
  }
  const toggleWeekday = (teIdx: number, day: string) => {
    const entry = tcForm.time_entries[teIdx]
    const days = entry.weekdays.includes(day) ? entry.weekdays.filter(d => d !== day) : [...entry.weekdays, day]
    updateTimeEntry(teIdx, 'weekdays', days)
  }

  // ============================================
  // Calendar cell-add flow helpers
  // ============================================

  // Convert a Set of "weekday-hour" cells (e.g. "tue-20") into TimeEntryForm[].
  // Strategy: per weekday compute contiguous hour ranges, then merge entries
  // with identical start/end across weekdays into a single entry with multiple weekdays.
  const buildEntriesFromCells = (cells: Set<string>): TimeEntryForm[] => {
    if (cells.size === 0) return []
    const byDay: Record<string, number[]> = {}
    for (const cell of cells) {
      const [day, hourStr] = cell.split('-')
      const h = parseInt(hourStr, 10)
      if (Number.isNaN(h)) continue
      ;(byDay[day] ||= []).push(h)
    }
    const dayRanges: Record<string, [number, number][]> = {}
    for (const day of Object.keys(byDay)) {
      const hours = [...new Set(byDay[day])].sort((a, b) => a - b)
      const ranges: [number, number][] = []
      let s = hours[0], e = hours[0] + 1
      for (let i = 1; i < hours.length; i++) {
        if (hours[i] === e) { e = hours[i] + 1 } else { ranges.push([s, e]); s = hours[i]; e = hours[i] + 1 }
      }
      ranges.push([s, e])
      dayRanges[day] = ranges
    }
    const order = ['mon','tue','wed','thu','fri','sat','sun']
    const rangeMap = new Map<string, { start: number; end: number; days: string[] }>()
    for (const day of Object.keys(dayRanges)) {
      for (const [s, e] of dayRanges[day]) {
        const key = `${s}-${e}`
        if (!rangeMap.has(key)) rangeMap.set(key, { start: s, end: e, days: [] })
        rangeMap.get(key)!.days.push(day)
      }
    }
    const entries: TimeEntryForm[] = []
    for (const r of rangeMap.values()) {
      const days = r.days.sort((a, b) => order.indexOf(a) - order.indexOf(b))
      const fmt = (h: number) => `${String(h).padStart(2,'0')}:00`
      entries.push({
        weekdays: days,
        start_time: fmt(r.start),
        end_time: r.end >= 24 ? '23:59' : fmt(r.end),
        start_date: '', end_date: '', date_mode: 'always',
        month_start: 1, day_of_month_start: 1, month_end: 1, day_of_month_end: 1,
        comment: ''
      })
    }
    return entries
  }

  const openEditTCWithExtraEntries = (tc: TimeCondition, extra: TimeEntryForm[]) => {
    setEditingTC(tc)
    setTCForm({
      name: tc.name, description: tc.description || '',
      match_action_type: tc.match_action_type || '', match_action_target: tc.match_action_target || '',
      match_intro_recording_id: tc.match_intro_recording_id || '',
      nomatch_action_type: tc.nomatch_action_type || '', nomatch_action_target: tc.nomatch_action_target || '',
      nomatch_intro_recording_id: tc.nomatch_intro_recording_id || '',
      priority: tc.priority || 10, is_active: tc.is_active,
      time_entries: [...timeEntriesToForm(tc.time_entries), ...extra]
    })
    setShowTCModal(true)
  }

  const openCreateTCWithEntries = (entries: TimeEntryForm[]) => {
    setEditingTC(null)
    const nextPrio = sortedTCs.length > 0 ? Math.max(...sortedTCs.map(t => t.priority || 10)) + 10 : 10
    setTCForm({ ...emptyTCForm, priority: nextPrio, time_entries: entries })
    setShowTCModal(true)
  }

  // --- Drag-selection: mousedown + drag over cells ---
  const finalizeDragSelection = (cells: Set<string>) => {
    if (cells.size === 0) return
    const entries = buildEntriesFromCells(cells)
    setSelectedCells(new Set())
    if (selectedCalendarTcId) {
      const tc = sortedTCs.find(t => t.id === selectedCalendarTcId)
      if (tc) { openEditTCWithExtraEntries(tc, entries); return }
    }
    openCreateTCWithEntries(entries)
  }

  // dragModeRef tracks whether we have a "real" drag (entered ≥1 different cell).
  // Set in mousedown but with `armed=true, dragged=false` semantics — only on first
  // mouseenter into ANOTHER cell do we update the visual selection. Otherwise a
  // simple click would mark the start cell + an adjacent cell whenever the cursor
  // shifts a single pixel between mousedown and mouseup.
  const dragStartedRef = useRef(false)
  const handleCellMouseDown = (date: Date, hour: number, e: React.MouseEvent) => {
    if (e.button !== 0) return  // only left button
    e.preventDefault()
    const dayValue = weekdays.find(w => w.index === date.getDay())?.value
    if (!dayValue) return
    const cellKey = `${dayValue}-${hour}`

    dragModeRef.current = e.shiftKey ? 'shift' : 'plain'
    dragCellsRef.current = new Set([cellKey])
    dragStartedRef.current = false  // no drag yet — just one cell
    // Visual change is deferred to either:
    //   - the first mouseenter into a different cell (= drag started), or
    //   - the document mouseup (= single click).
  }

  const handleCellMouseEnter = (date: Date, hour: number) => {
    if (!dragModeRef.current) return
    const dayValue = weekdays.find(w => w.index === date.getDay())?.value
    if (!dayValue) return
    const cellKey = `${dayValue}-${hour}`
    if (dragCellsRef.current.has(cellKey)) return
    dragCellsRef.current.add(cellKey)
    dragStartedRef.current = true
    // Live preview while dragging
    if (dragModeRef.current === 'shift') {
      setSelectedCells(prev => new Set([...prev, ...dragCellsRef.current]))
    } else {
      setSelectedCells(new Set(dragCellsRef.current))
    }
  }

  // Document-level mouseup so a drag that releases outside the table still ends.
  useEffect(() => {
    const handler = () => {
      const mode = dragModeRef.current
      if (!mode) return
      const cells = dragCellsRef.current
      const wasDrag = dragStartedRef.current
      dragModeRef.current = null
      dragCellsRef.current = new Set()
      dragStartedRef.current = false

      if (mode === 'shift') {
        // Shift: build up pending selection (drag adds many; click toggles one).
        if (wasDrag) {
          // Cells were already merged into selectedCells via mouseenter previews.
          return
        }
        // Single shift+click: toggle the one start cell.
        const [only] = cells
        if (!only) return
        setSelectedCells(prev => {
          const next = new Set(prev)
          if (next.has(only)) next.delete(only); else next.add(only)
          return next
        })
        return
      }

      // Plain mode → finalize and open dialog.
      // Combined set = pending shift selection (if any) + cells from this gesture.
      const all = wasDrag
        ? new Set(cells)                       // drag replaces — cells already shown
        : new Set([...selectedCells, ...cells]) // single click — extend pending then finalize
      finalizeDragSelection(all)
    }
    document.addEventListener('mouseup', handler)
    return () => document.removeEventListener('mouseup', handler)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCalendarTcId, sortedTCs, selectedCells])

  // ============================================
  // Calendar
  // ============================================

  const calendarTCs = useMemo(() =>
    sortedTCs.filter(tc => tc.is_active).map((tc, i) => ({ ...tc, color: COLORS[i % COLORS.length] })),
  [sortedTCs])

  // Only the highest-priority TC for a date (first match wins, calendarTCs is sorted by priority)
  const getTopConditionForDate = (date: Date) => {
    for (const tc of calendarTCs) {
      if (checkConditionApplies(tc, date)) return { condition: tc, color: tc.color }
    }
    return null
  }

  // For a given hour, find the highest-priority TC that covers it and return its coverage
  const getWinnerForHour = (date: Date, hour: number): { condition: typeof calendarTCs[number]; color: string; start: number; end: number } | null => {
    for (const tc of calendarTCs) {
      const coverage = getHourCoverage(tc, date, hour)
      if (coverage) return { condition: tc, color: tc.color, start: coverage.start, end: coverage.end }
    }
    return null
  }

  const navigatePrev = () => {
    const d = new Date(currentDate)
    if (calendarView === 'week') d.setDate(d.getDate() - 7)
    else if (calendarView === 'month') d.setMonth(d.getMonth() - 1)
    else d.setFullYear(d.getFullYear() - 1)
    setCurrentDate(d)
  }
  const navigateNext = () => {
    const d = new Date(currentDate)
    if (calendarView === 'week') d.setDate(d.getDate() + 7)
    else if (calendarView === 'month') d.setMonth(d.getMonth() + 1)
    else d.setFullYear(d.getFullYear() + 1)
    setCurrentDate(d)
  }
  const navigateToday = () => setCurrentDate(new Date())

  const weekData = useMemo(() => {
    const monday = getMonday(currentDate)
    const days: Date[] = []
    for (let i = 0; i < 7; i++) { const d = new Date(monday); d.setDate(monday.getDate() + i); days.push(d) }
    return { days, hours: Array.from({ length: 24 }, (_, i) => i), monday }
  }, [currentDate])

  const monthData = useMemo(() => {
    const year = currentDate.getFullYear(), month = currentDate.getMonth()
    const firstDay = new Date(year, month, 1), lastDay = new Date(year, month + 1, 0)
    const startDate = getMonday(firstDay)
    const weeks: Date[][] = []; let currentWeek: Date[] = []; const d = new Date(startDate)
    while (d <= lastDay || currentWeek.length > 0) {
      currentWeek.push(new Date(d))
      if (currentWeek.length === 7) { weeks.push(currentWeek); currentWeek = []; if (d > lastDay) break }
      d.setDate(d.getDate() + 1)
    }
    return { weeks, month, year }
  }, [currentDate])

  const yearData = useMemo(() => {
    const year = currentDate.getFullYear()
    const monthsData: { month: number; weeks: Date[][] }[] = []
    for (let m = 0; m < 12; m++) {
      const firstDay = new Date(year, m, 1), lastDay = new Date(year, m + 1, 0), startDate = getMonday(firstDay)
      const weeks: Date[][] = []; let cw: Date[] = []; const d = new Date(startDate)
      while (d <= lastDay || cw.length > 0) { cw.push(new Date(d)); if (cw.length === 7) { weeks.push(cw); cw = []; if (d > lastDay) break }; d.setDate(d.getDate() + 1) }
      monthsData.push({ month: m, weeks })
    }
    return { year, months: monthsData }
  }, [currentDate])

  const showLayerNomatchRecording = layerForm.nomatch_action_type && layerForm.nomatch_action_type !== 'hangup'
  const showTcMatchRecording = tcForm.match_action_type && tcForm.match_action_type !== 'hangup'
  const showTcNomatchRecording = tcForm.nomatch_action_type && tcForm.nomatch_action_type !== 'hangup'
  const hasNomatch = !!tcForm.nomatch_action_type

  // ============================================
  // RENDER
  // ============================================

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Zeitbedingungen</h4>
      </div>

      {/* === Layer selector (Bedingung dropdown) === */}
      <Card className="mb-3">
        <Card.Body className="py-2">
          <Row className="align-items-center">
            <Col md={6}>
              <Dropdown show={dropdownOpen} onToggle={(open) => { setDropdownOpen(open); if (open) setTimeout(() => searchRef.current?.focus(), 50) }}>
                <Dropdown.Toggle variant="outline-secondary" className="w-100 text-start d-flex justify-content-between align-items-center">
                  {selectedLayer ? (
                    <span>
                      <i className="bi bi-clock me-2"></i>
                      <strong>{selectedLayer.name}</strong>
                      <Badge bg={selectedLayer.is_active ? 'success' : 'secondary'} className="ms-2">{selectedLayer.is_active ? 'Aktiv' : 'Inaktiv'}</Badge>
                      <span className="text-muted ms-2">({(selectedLayer.time_conditions || []).length} Zeitbedingungen)</span>
                    </span>
                  ) : <span className="text-muted">Bedingung auswählen...</span>}
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <div className="px-2 pb-2">
                    <Form.Control ref={searchRef} size="sm" placeholder="Suchen..." value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)} onClick={(e) => e.stopPropagation()} />
                  </div>
                  {filteredLayers.length === 0 ? (
                    <Dropdown.ItemText className="text-muted">Keine Bedingungen gefunden</Dropdown.ItemText>
                  ) : filteredLayers.map(layer => (
                    <Dropdown.Item key={layer.id} active={layer.id === selectedLayerId}
                      onClick={() => { setSelectedLayerId(layer.id); setSearchTerm(''); setDropdownOpen(false) }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{layer.name}</strong>
                          {layer.description && <div className="text-muted small">{layer.description}</div>}
                        </div>
                        <div className="d-flex gap-1">
                          <Badge bg={layer.is_active ? 'success' : 'secondary'}>{layer.is_active ? 'Aktiv' : 'Inaktiv'}</Badge>
                          <Badge bg="info">{(layer.time_conditions || []).length}</Badge>
                        </div>
                      </div>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>
            <Col md={6} className="d-flex justify-content-end gap-2">
              {selectedLayer && (
                <>
                  <Button variant="primary" size="sm" onClick={openCreateTCModal}>
                    <i className="bi bi-plus-lg me-1"></i> Neue Zeitbedingung
                  </Button>
                  <Button variant="outline-primary" size="sm" onClick={() => openEditLayerModal(selectedLayer)}>
                    <i className="bi bi-gear me-1"></i> Einstellungen
                  </Button>
                  <Button variant="outline-danger" size="sm" onClick={() => handleDeleteLayer(selectedLayer)}>
                    <i className="bi bi-trash me-1"></i> Löschen
                  </Button>
                </>
              )}
              <Button variant="outline-success" size="sm" onClick={openCreateLayerModal}>
                <i className="bi bi-plus-lg me-1"></i> Neue Bedingung
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* === Content === */}
      {loading ? (
        <div className="text-center py-4">Laden...</div>
      ) : !selectedLayer ? (
        <Card className="text-center py-5">
          <Card.Body className="text-muted">
            <i className="bi bi-clock-history d-block mb-2" style={{ fontSize: '3rem' }}></i>
            {layers.length === 0 ? 'Noch keine Bedingungen vorhanden. Erstellen Sie eine neue Bedingung.' : 'Bitte wählen Sie eine Bedingung aus.'}
          </Card.Body>
        </Card>
      ) : (
        <>
          {/* Fallback info */}
          <div className="d-flex align-items-center gap-2 mb-3 small text-muted">
            <i className="bi bi-info-circle"></i>
            Zeitbedingungen werden nach Priorität geprüft (Wasserfall). Fallback:
            <Badge bg="warning" text="dark"><i className="bi bi-arrow-return-right me-1"></i>{selectedLayer.nomatch_action_type || 'Hangup'}</Badge>
          </div>

          <Tabs activeKey={activeTab} onSelect={(k) => { setActiveTab(k || 'list'); if (k === 'holidays' && !holidayConfig) loadHolidayConfig() }} className="mb-3">
            {/* ========= LIST TAB ========= */}
            <Tab eventKey="list" title={<><i className="bi bi-list-ul me-1"></i>Liste</>}>
              <div className="table-container">
                <Table hover className="mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: '60px' }}>Prio</th>
                      <th>Name</th>
                      <th>Tage</th>
                      <th>Zeitfenster</th>
                      <th>Gültigkeit</th>
                      <th>Match-Ziel</th>
                      <th>Status</th>
                      <th className="text-end" style={{ width: '180px' }}>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTCs.length === 0 ? (
                      <tr><td colSpan={8} className="text-center py-4 text-muted">Keine Zeitbedingungen vorhanden</td></tr>
                    ) : sortedTCs.map((tc, idx) => {
                      const manualEntries = (tc.time_entries || []).filter(e => !e.holiday_definition_id)
                      const holidayCount = (tc.time_entries || []).length - manualEntries.length
                      const firstEntry = manualEntries[0]
                      const hasDateRange = manualEntries.some(e => e.start_date || e.end_date)
                      return (
                        <tr key={tc.id} style={{ opacity: tc.is_active ? 1 : 0.5 }}>
                          <td><Badge style={{ backgroundColor: COLORS[idx % COLORS.length], minWidth: '32px' }}>{tc.priority || 10}</Badge></td>
                          <td>
                            <strong>{tc.name}</strong>
                            {tc.description && <div className="text-muted small">{tc.description}</div>}
                            {holidayCount > 0 && <Badge bg="info" className="ms-1" style={{ fontSize: '0.65rem' }}><i className="bi bi-calendar-heart me-1"></i>{holidayCount} Feiertage</Badge>}
                          </td>
                          <td>{firstEntry ? <span className="small">{formatWeekdaysBadge(firstEntry.weekdays)}</span> : holidayCount > 0 ? <span className="text-muted small">Nur Feiertage</span> : <span className="text-muted small">-</span>}</td>
                          <td>
                            {firstEntry?.start_time && firstEntry?.end_time ? <span className="small">{firstEntry.start_time.substring(0,5)} - {firstEntry.end_time.substring(0,5)}</span> : <span className="text-muted small">-</span>}
                            {manualEntries.length > 1 && <Badge bg="light" text="dark" className="ms-1">+{manualEntries.length - 1}</Badge>}
                          </td>
                          <td>
                            {hasDateRange ? (
                              <span className="text-muted small"><i className="bi bi-calendar-range me-1"></i>
                                {manualEntries.find(e => e.start_date)?.start_date || '...'} - {manualEntries.find(e => e.end_date)?.end_date || '...'}
                              </span>
                            ) : <span className="text-success small"><i className="bi bi-infinity me-1"></i>Immer</span>}
                          </td>
                          <td><Badge bg="outline-secondary" className="border text-dark small">{tc.match_action_type}{tc.match_action_target && `: ${tc.match_action_target}`}</Badge></td>
                          <td>
                            <Badge bg={tc.is_active ? 'success' : 'secondary'}>{tc.is_active ? 'Aktiv' : 'Inaktiv'}</Badge>
                            {tc.nomatch_action_type && (
                              <OverlayTrigger placement="top" overlay={<Tooltip>Eigene No-Match: {tc.nomatch_action_type}</Tooltip>}>
                                <Badge bg="warning" text="dark" className="ms-1"><i className="bi bi-stop-circle"></i></Badge>
                              </OverlayTrigger>
                            )}
                          </td>
                          <td className="text-end">
                            <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEditTCModal(tc)} title="Bearbeiten">
                              <i className="bi bi-pencil"></i>
                            </Button>
                            <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => openCloneModal(tc)} title="Klonen">
                              <i className="bi bi-copy"></i>
                            </Button>
                            <Button variant="outline-info" size="sm" className="me-1" onClick={() => openMoveModal(tc)} title="Verschieben">
                              <i className="bi bi-arrow-left-right"></i>
                            </Button>
                            <Button variant="outline-danger" size="sm" onClick={() => handleDeleteTC(tc)} title="Löschen">
                              <i className="bi bi-trash"></i>
                            </Button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              </div>
            </Tab>

            {/* ========= CALENDAR TAB ========= */}
            <Tab eventKey="calendar" title={<><i className="bi bi-calendar-week me-1"></i>Kalender</>}>
              <Card>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <ButtonGroup size="sm">
                      <Button variant="outline-secondary" onClick={navigatePrev}><i className="bi bi-chevron-left"></i></Button>
                      <Button variant="outline-secondary" onClick={navigateToday}>Heute</Button>
                      <Button variant="outline-secondary" onClick={navigateNext}><i className="bi bi-chevron-right"></i></Button>
                    </ButtonGroup>
                    <h5 className="mb-0">
                      {calendarView === 'week' && <>KW: {formatDate(weekData.days[0])} - {formatDate(weekData.days[6])}</>}
                      {calendarView === 'month' && <>{months[monthData.month]} {monthData.year}</>}
                      {calendarView === 'year' && <>{yearData.year}</>}
                    </h5>
                    <ButtonGroup size="sm">
                      <Button variant={calendarView === 'week' ? 'primary' : 'outline-secondary'} onClick={() => setCalendarView('week')}>Woche</Button>
                      <Button variant={calendarView === 'month' ? 'primary' : 'outline-secondary'} onClick={() => setCalendarView('month')}>Monat</Button>
                      <Button variant={calendarView === 'year' ? 'primary' : 'outline-secondary'} onClick={() => setCalendarView('year')}>Jahr</Button>
                    </ButtonGroup>
                  </div>
                  {/* Legend - badges are clickable to select a TC for cell-add mode */}
                  <div className="d-flex flex-wrap gap-2 mb-2 align-items-center">
                    {calendarTCs.length === 0
                      ? <span className="text-muted">Keine aktiven Zeitbedingungen</span>
                      : calendarTCs.map((tc, i) => {
                          const active = selectedCalendarTcId === tc.id
                          return (
                            <Badge
                              key={i}
                              role="button"
                              onClick={() => { setSelectedCalendarTcId(active ? null : tc.id); setSelectedCells(new Set()) }}
                              style={{
                                backgroundColor: tc.color,
                                cursor: 'pointer',
                                outline: active ? '2px solid #000' : undefined,
                                outlineOffset: active ? '2px' : undefined,
                                opacity: selectedCalendarTcId && !active ? 0.5 : 1,
                              }}
                              title={active ? 'Auswahl aufheben' : 'Diese Zeitbedingung wählen, dann auf Kalender-Zellen klicken'}
                            >
                              {tc.priority}. {tc.name}
                            </Badge>
                          )
                        })
                    }
                    {(selectedCalendarTcId || selectedCells.size > 0) && (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="ms-auto"
                        onClick={() => { setSelectedCalendarTcId(null); setSelectedCells(new Set()) }}
                      >
                        <i className="bi bi-x-lg me-1"></i>Auswahl aufheben
                      </Button>
                    )}
                  </div>
                  {calendarView === 'week' && (
                    <div className="small text-muted mb-2">
                      <i className="bi bi-info-circle me-1"></i>
                      {selectedCalendarTcId
                        ? <>Klick auf eine Stunden-Zelle fügt ein neues Zeitfenster zur gewählten Zeitbedingung hinzu. Mit <kbd>Shift</kbd>+Klick mehrere Felder markieren.</>
                        : <>Tipp: Erst auf einen Badge oben klicken, dann auf Kalender-Zellen klicken — so fügst du der Zeitbedingung neue Zeitfenster hinzu. Ohne Auswahl wird eine neue Zeitbedingung erstellt. <kbd>Shift</kbd>+Klick = Mehrfachauswahl.</>
                      }
                      {selectedCells.size > 0 && <span className="ms-2 text-primary fw-bold">{selectedCells.size} Felder markiert</span>}
                    </div>
                  )}
                  {/* Week */}
                  {calendarView === 'week' && (
                    <div className="table-responsive" style={{ userSelect: 'none', WebkitUserSelect: 'none' }} onMouseDown={(e) => { if (e.shiftKey) e.preventDefault() }}>
                      <table className="table table-bordered table-sm mb-0" style={{ fontSize: '0.75rem' }}>
                        <thead><tr><th style={{ width: '50px' }}>Uhr</th>
                          {weekData.days.map((d, i) => <th key={i} className="text-center"><div>{weekdays[i].fullLabel}</div><small className="text-muted">{formatDate(d)}</small></th>)}
                        </tr></thead>
                        <tbody>
                          {weekData.hours.map(hour => (
                            <tr key={hour}>
                              <td className="text-muted text-end pe-2" style={{ verticalAlign: 'top', lineHeight: '28px' }}>{hour.toString().padStart(2,'0')}:00</td>
                              {weekData.days.map((date, i) => {
                                const winner = getWinnerForHour(date, hour)
                                const dayValue = weekdays.find(w => w.index === date.getDay())?.value || ''
                                const cellKey = `${dayValue}-${hour}`
                                const isSelected = selectedCells.has(cellKey)
                                return <td
                                  key={i}
                                  onMouseDown={(e) => handleCellMouseDown(date, hour, e)}
                                  onMouseEnter={() => handleCellMouseEnter(date, hour)}
                                  style={{
                                    padding: 0,
                                    height: '28px',
                                    position: 'relative',
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? '#cfe2ff' : undefined,
                                    boxShadow: isSelected ? 'inset 0 0 0 2px #0d6efd' : undefined,
                                  }}
                                >
                                  {winner && (
                                    <OverlayTrigger placement="top" overlay={
                                      <Tooltip>
                                        <strong>{winner.condition.name}</strong> (Prio {winner.condition.priority})
                                        <div className="small">
                                          {(() => {
                                            const dayValue = weekdays.find(w => w.index === date.getDay())?.value || ''
                                            const dateStr = formatDateISO(date)
                                            const matching = (winner.condition.time_entries || []).filter(te => checkEntryDateMatch(te, date, dateStr, dayValue))
                                            const holiday = matching.find(te => te.holiday_definition_id)
                                            if (holiday) return <><i className="bi bi-calendar-heart me-1"></i>{holiday.comment || 'Feiertag'}</>
                                            const times = matching.filter(te => te.start_time && te.end_time).map(te => `${te.start_time!.substring(0,5)}-${te.end_time!.substring(0,5)}`)
                                            return times.length > 0 ? times.join(', ') : 'Ganztägig'
                                          })()}
                                        </div>
                                      </Tooltip>
                                    }>
                                      <div
                                        onMouseDown={(e) => {
                                          // In cell-add/drag mode (shift, TC pre-selected, or pending cells)
                                          // we want the underlying <td> mousedown to start the drag — let it bubble.
                                          if (e.shiftKey || selectedCalendarTcId || selectedCells.size > 0) return
                                          // Otherwise treat this as "click to edit existing TC" — block the
                                          // td's drag start so we don't accidentally also create a selection.
                                          e.stopPropagation()
                                        }}
                                        onClick={(e) => {
                                          // If we were in cell-add mode, the drag handler already finalized
                                          // and opened the dialog. Don't ALSO open the edit dialog here.
                                          if (e.shiftKey || selectedCalendarTcId || selectedCells.size > 0) {
                                            e.stopPropagation()
                                            return
                                          }
                                          e.stopPropagation()
                                          const full = sortedTCs.find(t => t.id === winner.condition.id)
                                          if (full) openEditTCModal(full)
                                        }}
                                        style={{
                                        position: 'absolute',
                                        left: 0,
                                        width: '100%',
                                        top: `${winner.start * 100}%`,
                                        height: `${(winner.end - winner.start) * 100}%`,
                                        backgroundColor: winner.color + '50',
                                        borderLeft: `3px solid ${winner.color}`,
                                        borderTop: winner.start > 0 ? `1px solid ${winner.color}80` : undefined,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        paddingLeft: '4px',
                                        overflow: 'hidden',
                                      }}>
                                        <span className="text-truncate" style={{ fontSize: '0.65rem', color: winner.color, fontWeight: 600 }}>
                                          {winner.condition.name}
                                        </span>
                                      </div>
                                    </OverlayTrigger>
                                  )}
                                </td>
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {/* Month */}
                  {calendarView === 'month' && (
                    <div className="table-responsive">
                      <table className="table table-bordered mb-0">
                        <thead><tr>{weekdays.map(wd => <th key={wd.value} className="text-center" style={{ width: '14.28%' }}>{wd.fullLabel}</th>)}</tr></thead>
                        <tbody>
                          {monthData.weeks.map((week, wi) => (
                            <tr key={wi}>{week.map((date, di) => {
                              const isCur = date.getMonth() === monthData.month
                              const top = isCur ? getTopConditionForDate(date) : null
                              const isToday = formatDateISO(date) === formatDateISO(new Date())
                              return <td key={di} style={{ height: '80px', verticalAlign: 'top', backgroundColor: !isCur ? '#f8f9fa' : top ? top.color+'20' : 'white', opacity: isCur ? 1 : 0.5 }}>
                                <div className={`fw-bold mb-1 ${isToday ? 'text-primary' : ''}`}>{date.getDate()}</div>
                                {isCur && top && (() => {
                                  const dayValue = weekdays.find(w => w.index === date.getDay())?.value || ''
                                  const dateStr = formatDateISO(date)
                                  const holiday = (top.condition.time_entries || []).find(te => te.holiday_definition_id && checkEntryDateMatch(te, date, dateStr, dayValue))
                                  const label = holiday ? holiday.comment || 'Feiertag' : top.condition.name
                                  return (
                                    <OverlayTrigger placement="top" overlay={<Tooltip>{label} ({top.condition.name}, Prio {top.condition.priority})</Tooltip>}>
                                      <div
                                        onClick={() => {
                                          const full = sortedTCs.find(t => t.id === top.condition.id)
                                          if (full) openEditTCModal(full)
                                        }}
                                        className="text-truncate small px-1 rounded"
                                        style={{ backgroundColor: top.color+'40', borderLeft: `3px solid ${top.color}`, cursor: 'pointer' }}>{label}</div>
                                    </OverlayTrigger>
                                  )
                                })()}
                              </td>
                            })}</tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {/* Year */}
                  {calendarView === 'year' && (
                    <Row>{yearData.months.map(({ month, weeks }) => (
                      <Col key={month} md={4} lg={3} className="mb-4">
                        <Card className="h-100"><Card.Header className="py-1 text-center fw-bold" style={{ fontSize: '0.85rem' }}>{months[month]}</Card.Header>
                          <Card.Body className="p-1">
                            <table className="table table-sm table-borderless mb-0" style={{ fontSize: '0.65rem' }}>
                              <thead><tr>{weekdays.map(wd => <th key={wd.value} className="text-center p-0">{wd.label}</th>)}</tr></thead>
                              <tbody>{weeks.map((week, wi) => <tr key={wi}>{week.map((date, di) => {
                                const isCur = date.getMonth() === month
                                const top = isCur ? getTopConditionForDate(date) : null
                                return <td key={di} className="text-center p-0" style={{ backgroundColor: isCur && top ? top.color+'60' : 'transparent', opacity: isCur ? 1 : 0.3, borderRadius: '2px' }}>
                                  <OverlayTrigger placement="top" overlay={<Tooltip>{formatDate(date)}{top && <div>{top.condition.name} (Prio {top.condition.priority})</div>}</Tooltip>}>
                                    <span
                                      onClick={() => {
                                        if (!top) return
                                        const full = sortedTCs.find(t => t.id === top.condition.id)
                                        if (full) openEditTCModal(full)
                                      }}
                                      style={{ cursor: top ? 'pointer' : 'default' }}>{date.getDate()}</span>
                                  </OverlayTrigger>
                                </td>
                              })}</tr>)}</tbody>
                            </table>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}</Row>
                  )}
                </Card.Body>
              </Card>
            </Tab>

            {/* ========= HOLIDAYS TAB ========= */}
            <Tab eventKey="holidays" title={<><i className="bi bi-calendar-heart me-1"></i>Feiertage</>}>
              <Row>
                {/* Left: Configuration */}
                <Col md={5}>
                  <Card className="mb-3">
                    <Card.Header className="bg-light"><i className="bi bi-gear me-2"></i>Kanton &amp; Feiertage</Card.Header>
                    <Card.Body>
                      <Form.Group className="mb-3">
                        <Form.Label>Kanton</Form.Label>
                        <Form.Select value={holidayConfig?.canton || ''} onChange={(e) => handleCantonChange(e.target.value)} disabled={holidayLoading}>
                          <option value="">-- Kein Kanton --</option>
                          {cantons.map(c => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
                        </Form.Select>
                        <Form.Text className="text-muted">Wählen Sie den Kanton für die gesetzlichen Feiertage</Form.Text>
                      </Form.Group>
                      {holidayConfig?.canton && subdivisions.length > 0 && (
                        <Form.Group className="mb-3">
                          <Form.Label>Gemeinde / Bezirk <small className="text-muted">(optional)</small></Form.Label>
                          <Form.Select
                            value={holidayConfig?.subdivision_code || ''}
                            onChange={(e) => handleSubdivisionChange(e.target.value)}
                            disabled={holidayLoading}
                          >
                            <option value="">-- Kanton-Default ({holidayConfig.canton}) --</option>
                            <optgroup label="Bezirke">
                              {subdivisions.filter(s => s.depth === 1).map(s => (
                                <option key={s.code} value={s.code}>{s.name}</option>
                              ))}
                            </optgroup>
                            {subdivisions.filter(s => s.depth >= 2).length > 0 && (
                              <optgroup label="Gemeinden">
                                {subdivisions.filter(s => s.depth >= 2).map(s => {
                                  const parent = subdivisions.find(p => p.code === s.parent_code)
                                  return (
                                    <option key={s.code} value={s.code}>
                                      {s.name}{parent ? ` (${parent.name})` : ''}
                                    </option>
                                  )
                                })}
                              </optgroup>
                            )}
                          </Form.Select>
                          <Form.Text className="text-muted">
                            Präzisiert die Feiertage auf eine Gemeinde — z.B. in SO bekommt Dornach Fronleichnam,
                            aber rein reformierte Gemeinden nicht. Leer lassen für Kanton-Default.
                          </Form.Text>
                        </Form.Group>
                      )}
                      <Form.Group className="mb-3">
                        <Form.Check type="switch" id="auto-regenerate-holidays"
                          checked={holidayConfig?.auto_regenerate || false}
                          disabled={!holidayConfig?.canton || holidayLoading}
                          onChange={(e) => handleToggleAutoRegenerate(e.target.checked)}
                          label={<><strong>Automatisch jährlich aktualisieren</strong></>} />
                        <Form.Text className="text-muted d-block">
                          Wenn aktiviert, werden Feiertage für alle Zeitbedingungen (die bereits Feiertage nutzen) automatisch jeden Tag um 02:00 Uhr für das aktuelle und nächste Jahr neu generiert. So müssen Sie sich um nichts kümmern.
                        </Form.Text>
                      </Form.Group>

                      {holidayConfig?.holiday_definitions && holidayConfig.holiday_definitions.length > 0 && (
                        <>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong className="small">Feiertage</strong>
                            <Button variant="outline-primary" size="sm" onClick={() => {
                              setCustomHolidayForm({ name: '', holiday_type: 'FIXED', fixed_month: 1, fixed_day: 1, easter_offset: 0, custom_date: '', custom_recurs_yearly: true })
                              setShowCustomHolidayModal(true)
                            }}>
                              <i className="bi bi-plus-lg me-1"></i>Eigener Feiertag
                            </Button>
                          </div>
                          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {holidayConfig.holiday_definitions.map(d => (
                              <div key={d.id} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                <Form.Check type="switch" id={`hol-${d.id}`} checked={d.is_enabled}
                                  onChange={() => handleToggleHoliday(d)}
                                  label={<span className={d.is_enabled ? '' : 'text-muted text-decoration-line-through'}>{d.name}
                                    {d.is_system && <Badge bg="secondary" className="ms-1" style={{ fontSize: '0.65rem' }}>System</Badge>}
                                    {!d.is_system && <Badge bg="info" className="ms-1" style={{ fontSize: '0.65rem' }}>Eigener</Badge>}
                                  </span>} />
                                {!d.is_system && (
                                  <Button variant="link" size="sm" className="text-danger p-0" onClick={() => handleDeleteCustomHoliday(d.id)}>
                                    <i className="bi bi-trash"></i>
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </Card.Body>
                  </Card>

                  {/* Generate for TC */}
                  <Card>
                    <Card.Header className="bg-light"><i className="bi bi-lightning me-2"></i>Feiertage zuweisen</Card.Header>
                    <Card.Body className="p-0">
                      {sortedTCs.length === 0 ? (
                        <div className="text-muted text-center py-3 small">Keine Zeitbedingungen in dieser Bedingung</div>
                      ) : (
                        <Table size="sm" className="mb-0">
                          <tbody>
                            {sortedTCs.map(tc => {
                              const holidayCount = (tc.time_entries || []).filter(e => e.holiday_definition_id).length
                              return (
                                <tr key={tc.id}>
                                  <td style={{ verticalAlign: 'middle' }}>
                                    <strong>{tc.name}</strong>
                                    {holidayCount > 0 && (
                                      <Badge bg="info" className="ms-2" style={{ fontSize: '0.65rem' }}>
                                        <i className="bi bi-calendar-heart me-1"></i>{holidayCount} Feiertage
                                      </Badge>
                                    )}
                                  </td>
                                  <td className="text-end" style={{ width: '180px' }}>
                                    <Button variant="outline-primary" size="sm" className="me-1"
                                      onClick={async () => {
                                        setGenerateTCId(tc.id)
                                        setHolidayLoading(true)
                                        try {
                                          const r = await holidaysApi.generate({ time_condition_id: tc.id, years: [previewYear, previewYear + 1] })
                                          toast.success(`${r.data.generated} Feiertags-Einträge generiert`)
                                          loadLayers()
                                        } catch { toast.error('Fehler beim Generieren') }
                                        finally { setHolidayLoading(false) }
                                      }}
                                      disabled={holidayLoading}
                                      title="Feiertage generieren/aktualisieren">
                                      <i className="bi bi-lightning"></i>
                                    </Button>
                                    {holidayCount > 0 && (
                                      <Button variant="outline-danger" size="sm"
                                        onClick={() => handleRemoveGeneratedHolidays(tc.id)}
                                        title="Alle Feiertags-Einträge entfernen">
                                        <i className="bi bi-trash"></i>
                                      </Button>
                                    )}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </Table>
                      )}
                    </Card.Body>
                    <Card.Footer className="small text-muted py-2">
                      <i className="bi bi-info-circle me-1"></i>
                      Blitz = Feiertage für {previewYear}/{previewYear + 1} generieren. Papierkorb = alle Feiertage aus dieser TC entfernen.
                    </Card.Footer>
                  </Card>
                </Col>

                {/* Right: Preview */}
                <Col md={7}>
                  <Card>
                    <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                      <span><i className="bi bi-eye me-2"></i>Vorschau</span>
                      <div className="d-flex gap-1 align-items-center">
                        <Button variant="outline-secondary" size="sm" onClick={() => { const y = previewYear - 1; setPreviewYear(y); loadHolidayPreview(y) }}>
                          <i className="bi bi-chevron-left"></i>
                        </Button>
                        <strong>{previewYear}</strong>
                        <Button variant="outline-secondary" size="sm" onClick={() => { const y = previewYear + 1; setPreviewYear(y); loadHolidayPreview(y) }}>
                          <i className="bi bi-chevron-right"></i>
                        </Button>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-0">
                      {holidayPreview.length === 0 ? (
                        <div className="text-center text-muted py-4">
                          {holidayConfig?.canton ? 'Keine Feiertage für dieses Jahr' : 'Wählen Sie zuerst einen Kanton'}
                        </div>
                      ) : (
                        <Table hover size="sm" className="mb-0">
                          <thead><tr><th>Datum</th><th>Wochentag</th><th>Feiertag</th><th>Typ</th></tr></thead>
                          <tbody>
                            {holidayPreview.map((h, i) => (
                              <tr key={i}>
                                <td>{new Date(h.date + 'T00:00:00').toLocaleDateString('de-CH')}</td>
                                <td>{h.weekday}</td>
                                <td>{h.name}</td>
                                <td><Badge bg={h.is_system ? 'secondary' : 'info'}>{h.is_system ? 'Kanton' : 'Eigener'}</Badge></td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>
          </Tabs>
        </>
      )}

      {/* ========= LAYER MODAL (Settings) ========= */}
      <Modal show={showLayerModal} onHide={() => setShowLayerModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingLayer ? 'Bedingung bearbeiten' : 'Neue Bedingung'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleLayerSubmit}>
          <Modal.Body>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group><Form.Label>Name *</Form.Label>
                  <Form.Control value={layerForm.name} onChange={(e) => setLayerForm({ ...layerForm, name: e.target.value })} required placeholder="z.B. Bürozeiten" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group><Form.Label>Beschreibung</Form.Label>
                  <Form.Control value={layerForm.description} onChange={(e) => setLayerForm({ ...layerForm, description: e.target.value })} placeholder="Optional" />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group><Form.Label>Status</Form.Label>
                  <Form.Check type="switch" id="layer_active" checked={layerForm.is_active}
                    onChange={(e) => setLayerForm({ ...layerForm, is_active: e.target.checked })} label={layerForm.is_active ? 'Aktiv' : 'Inaktiv'} />
                </Form.Group>
              </Col>
            </Row>
            <Card>
              <Card.Header className="bg-warning"><i className="bi bi-arrow-return-right me-2"></i>Fallback (wenn keine Zeitbedingung zutrifft) *</Card.Header>
              <Card.Body>
                <Form.Group className="mb-2">
                  <DestinationSelector destinationType={layerForm.nomatch_action_type} destinationValue={layerForm.nomatch_action_target}
                    onChange={(type, value) => setLayerForm({ ...layerForm, nomatch_action_type: type, nomatch_action_target: value, nomatch_intro_recording_id: type === 'hangup' ? '' : layerForm.nomatch_intro_recording_id })}
                    allowedTypes={TC_ALLOWED_TYPES} placeholder="Fallback-Ziel..." allowExternal={true} />
                </Form.Group>
                {showLayerNomatchRecording && (
                  <Form.Group>
                    <Form.Label className="small"><i className="bi bi-mic me-1"></i>Ansage vor Fallback</Form.Label>
                    <Form.Select size="sm" value={layerForm.nomatch_intro_recording_id} onChange={(e) => setLayerForm({ ...layerForm, nomatch_intro_recording_id: e.target.value })}>
                      <option value="">-- Keine Ansage --</option>
                      {recordings.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </Form.Select>
                  </Form.Group>
                )}
              </Card.Body>
            </Card>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowLayerModal(false)}>Abbrechen</Button>
            <Button variant="primary" type="submit">{editingLayer ? 'Speichern' : 'Erstellen'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ========= TC MODAL (single Zeitbedingung) ========= */}
      <Modal show={showTCModal} onHide={() => setShowTCModal(false)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>{editingTC ? `Zeitbedingung bearbeiten` : 'Neue Zeitbedingung'}</Modal.Title>
        </Modal.Header>
        {/* Form must not break the modal-content flex layout that Bootstrap's
            scrollable prop relies on — pass through flex sizing to the body. */}
        <Form
          onSubmit={handleTCSubmit}
          style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}
        >
          <Modal.Body>
            <Row className="mb-3">
              <Col md={5}>
                <Form.Group><Form.Label>Name *</Form.Label>
                  <Form.Control value={tcForm.name} onChange={(e) => setTCForm({ ...tcForm, name: e.target.value })} required placeholder="z.B. Feiertage, Geschäftszeiten" />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group><Form.Label>Beschreibung</Form.Label>
                  <Form.Control value={tcForm.description} onChange={(e) => setTCForm({ ...tcForm, description: e.target.value })} placeholder="Optional" />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group><Form.Label>Priorität</Form.Label>
                  <Form.Control type="number" min={1} max={100} value={tcForm.priority} onChange={(e) => setTCForm({ ...tcForm, priority: parseInt(e.target.value) || 10 })} />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group><Form.Label>Status</Form.Label>
                  <Form.Check type="switch" checked={tcForm.is_active} onChange={(e) => setTCForm({ ...tcForm, is_active: e.target.checked })} label={tcForm.is_active ? 'Aktiv' : 'Inaktiv'} />
                </Form.Group>
              </Col>
            </Row>

            {/* Time Entries */}
            <Card className="mb-3">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span><i className="bi bi-clock me-2"></i>Zeitfenster</span>
                <Button variant="outline-primary" size="sm" onClick={addTimeEntry}><i className="bi bi-plus-lg me-1"></i> Zeitfenster</Button>
              </Card.Header>
              <Card.Body>
                {tcForm.time_entries.length === 0 ? (
                  <p className="text-muted mb-0">Keine Zeitfenster definiert.</p>
                ) : (() => {
                  const manual = tcForm.time_entries.map((e, i) => ({ e, i })).filter(x => !x.e.holiday_definition_id)
                  const holidays = tcForm.time_entries.map((e, i) => ({ e, i })).filter(x => x.e.holiday_definition_id)
                  return <>
                    {manual.map(({ e: entry, i: teIdx }) => (
                      <Card key={teIdx} className="mb-2 border">
                        <Card.Body className="py-2 px-3">
                          <Row className="align-items-center mb-2">
                            <Col md={6}>
                              <div className="d-flex gap-1 flex-wrap">
                                {weekdays.map(wd => (
                                  <Button key={wd.value} size="sm" variant={entry.weekdays.includes(wd.value) ? 'primary' : 'outline-secondary'}
                                    onClick={() => toggleWeekday(teIdx, wd.value)} style={{ minWidth: '36px' }}>{wd.label}</Button>
                                ))}
                              </div>
                            </Col>
                            <Col md={2}><Form.Control type="time" size="sm" value={entry.start_time} onChange={(e) => updateTimeEntry(teIdx, 'start_time', e.target.value)} /></Col>
                            <Col md={1} className="text-center"><span className="text-muted">bis</span></Col>
                            <Col md={2}><Form.Control type="time" size="sm" value={entry.end_time} onChange={(e) => updateTimeEntry(teIdx, 'end_time', e.target.value)} /></Col>
                            <Col md={1} className="text-end"><Button variant="outline-danger" size="sm" onClick={() => removeTimeEntry(teIdx)}><i className="bi bi-trash"></i></Button></Col>
                          </Row>
                          <Row className="align-items-center">
                            <Col md={3}><Form.Select size="sm" value={entry.date_mode} onChange={(e) => updateTimeEntry(teIdx, 'date_mode', e.target.value)}>
                              <option value="always">Immer gültig</option><option value="range">Zeitraum</option><option value="yearly">Jährlich</option>
                            </Form.Select></Col>
                            {entry.date_mode === 'range' && (<>
                              <Col md={3}><Form.Control type="date" size="sm" value={entry.start_date} onChange={(e) => updateTimeEntry(teIdx, 'start_date', e.target.value)} /></Col>
                              <Col md={1} className="text-center"><span className="text-muted">bis</span></Col>
                              <Col md={3}><Form.Control type="date" size="sm" value={entry.end_date} onChange={(e) => updateTimeEntry(teIdx, 'end_date', e.target.value)} /></Col>
                            </>)}
                            {entry.date_mode === 'yearly' && (<>
                              <Col md={2}><Form.Select size="sm" value={entry.day_of_month_start} onChange={(e) => updateTimeEntry(teIdx, 'day_of_month_start', parseInt(e.target.value))}>
                                {Array.from({length:31},(_,i)=>i+1).map(d => <option key={d} value={d}>{d}.</option>)}
                              </Form.Select></Col>
                              <Col md={2}><Form.Select size="sm" value={entry.month_start} onChange={(e) => updateTimeEntry(teIdx, 'month_start', parseInt(e.target.value))}>
                                {['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'].map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
                              </Form.Select></Col>
                              <Col md={1} className="text-center"><span className="text-muted">bis</span></Col>
                              <Col md={2}><Form.Select size="sm" value={entry.day_of_month_end} onChange={(e) => updateTimeEntry(teIdx, 'day_of_month_end', parseInt(e.target.value))}>
                                {Array.from({length:31},(_,i)=>i+1).map(d => <option key={d} value={d}>{d}.</option>)}
                              </Form.Select></Col>
                              <Col md={2}><Form.Select size="sm" value={entry.month_end} onChange={(e) => updateTimeEntry(teIdx, 'month_end', parseInt(e.target.value))}>
                                {['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'].map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
                              </Form.Select></Col>
                            </>)}
                            <Col><Form.Control type="text" size="sm" placeholder="Kommentar" value={entry.comment} onChange={(e) => updateTimeEntry(teIdx, 'comment', e.target.value)} /></Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    ))}
                    {holidays.length > 0 && (
                      <Card className="mb-2 border border-info">
                        <Card.Header className="bg-info bg-opacity-10 py-1 px-3 d-flex justify-content-between align-items-center">
                          <span className="small"><i className="bi bi-calendar-heart me-2"></i><strong>Feiertage</strong> ({holidays.length}) — automatisch generiert, schreibgeschützt</span>
                          <span className="small text-muted">Verwaltung im Tab "Feiertage"</span>
                        </Card.Header>
                        <Card.Body className="py-1 px-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {holidays.sort((a, b) => (a.e.start_date || '').localeCompare(b.e.start_date || '')).map(({ e: entry, i: teIdx }) => (
                            <div key={teIdx} className="d-flex justify-content-between align-items-center py-1 border-bottom small">
                              <span>
                                <i className="bi bi-calendar-event text-info me-2"></i>
                                <strong>{entry.comment || 'Feiertag'}</strong>
                                <span className="text-muted ms-2">
                                  {entry.start_date && new Date(entry.start_date + 'T00:00:00').toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', weekday: 'short' })}
                                </span>
                              </span>
                              <Badge bg="secondary" style={{ fontSize: '0.65rem' }}>ganztägig</Badge>
                            </div>
                          ))}
                        </Card.Body>
                      </Card>
                    )}
                  </>
                })()}
              </Card.Body>
            </Card>

            {/* Match Action */}
            <Row>
              <Col md={6}>
                <Card className="mb-3">
                  <Card.Header className="bg-success text-white"><i className="bi bi-check-circle me-2"></i>Bei Übereinstimmung (Match)</Card.Header>
                  <Card.Body>
                    <Form.Group className="mb-2">
                      <DestinationSelector destinationType={tcForm.match_action_type} destinationValue={tcForm.match_action_target}
                        onChange={(type, value) => setTCForm({ ...tcForm, match_action_type: type, match_action_target: value, match_intro_recording_id: type === 'hangup' ? '' : tcForm.match_intro_recording_id })}
                        allowedTypes={TC_ALLOWED_TYPES} placeholder="Ziel bei Match..." allowExternal={true} />
                    </Form.Group>
                    {showTcMatchRecording && (
                      <Form.Group><Form.Label className="small"><i className="bi bi-mic me-1"></i>Ansage vor Ziel</Form.Label>
                        <Form.Select size="sm" value={tcForm.match_intro_recording_id} onChange={(e) => setTCForm({ ...tcForm, match_intro_recording_id: e.target.value })}>
                          <option value="">-- Keine --</option>
                          {recordings.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </Form.Select>
                      </Form.Group>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="mb-3">
                  <Card.Header className="bg-warning">
                    <div className="d-flex justify-content-between align-items-center">
                      <span><i className="bi bi-x-circle me-2"></i>Bei Nicht-Übereinstimmung</span>
                      <Form.Check type="switch" id="tc-nomatch-switch" checked={hasNomatch} label=""
                        onChange={(e) => {
                          if (e.target.checked) setTCForm({ ...tcForm, nomatch_action_type: 'hangup', nomatch_action_target: '', nomatch_intro_recording_id: '' })
                          else setTCForm({ ...tcForm, nomatch_action_type: '', nomatch_action_target: '', nomatch_intro_recording_id: '' })
                        }} />
                    </div>
                  </Card.Header>
                  <Card.Body>
                    {!hasNomatch ? (
                      <div className="text-muted small"><i className="bi bi-arrow-down me-1"></i>Kein Match: nächste Zeitbedingung in der Reihenfolge prüfen (Wasserfall)</div>
                    ) : (<>
                      <div className="text-muted small mb-2"><i className="bi bi-stop-circle me-1"></i>Stoppt den Wasserfall - keine weitere Prüfung</div>
                      <Form.Group className="mb-2">
                        <DestinationSelector destinationType={tcForm.nomatch_action_type} destinationValue={tcForm.nomatch_action_target}
                          onChange={(type, value) => setTCForm({ ...tcForm, nomatch_action_type: type, nomatch_action_target: value, nomatch_intro_recording_id: type === 'hangup' ? '' : tcForm.nomatch_intro_recording_id })}
                          allowedTypes={TC_ALLOWED_TYPES} placeholder="Ziel bei No-Match..." allowExternal={true} />
                      </Form.Group>
                      {showTcNomatchRecording && (
                        <Form.Group><Form.Label className="small"><i className="bi bi-mic me-1"></i>Ansage vor Ziel</Form.Label>
                          <Form.Select size="sm" value={tcForm.nomatch_intro_recording_id} onChange={(e) => setTCForm({ ...tcForm, nomatch_intro_recording_id: e.target.value })}>
                            <option value="">-- Keine --</option>
                            {recordings.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                          </Form.Select>
                        </Form.Group>
                      )}
                    </>)}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            {editingTC && (
              <Button
                variant="outline-danger"
                className="me-auto"
                onClick={async () => {
                  await handleDeleteTC(editingTC)
                  setShowTCModal(false)
                }}
              >
                <i className="bi bi-trash me-1"></i>Zeitbedingung löschen
              </Button>
            )}
            <Button variant="secondary" onClick={() => setShowTCModal(false)}>Abbrechen</Button>
            <Button variant="primary" type="submit">{editingTC ? 'Speichern' : 'Erstellen'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ========= MOVE MODAL ========= */}
      <Modal show={showMoveModal} onHide={() => setShowMoveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Zeitbedingung verschieben</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>"{movingTC?.name}"</strong> in eine andere Bedingung verschieben:
          </p>
          <Form.Select value={moveTargetLayerId} onChange={(e) => setMoveTargetLayerId(e.target.value)}>
            <option value="">Ziel-Bedingung wählen...</option>
            {layers.filter(l => l.id !== selectedLayerId).map(l => (
              <option key={l.id} value={l.id}>{l.name} ({(l.time_conditions || []).length} Zeitbedingungen)</option>
            ))}
          </Form.Select>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMoveModal(false)}>Abbrechen</Button>
          <Button variant="primary" disabled={!moveTargetLayerId} onClick={handleMoveTC}>Verschieben</Button>
        </Modal.Footer>
      </Modal>

      {/* ========= CLONE MODAL ========= */}
      <Modal show={showCloneModal} onHide={() => setShowCloneModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Zeitbedingung klonen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>"{cloningTC?.name}"</strong> klonen in:
          </p>
          <Form.Select value={cloneTargetLayerId} onChange={(e) => setCloneTargetLayerId(e.target.value)}>
            <option value="">Ziel-Bedingung wählen...</option>
            {layers.map(l => (
              <option key={l.id} value={l.id}>
                {l.name}
                {l.id === selectedLayerId ? ' (eigene Bedingung)' : ''}
                {' '}({(l.time_conditions || []).length} Zeitbedingungen)
              </option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">
            Im Gegensatz zum Verschieben kann hier auch in die <strong>eigene Bedingung</strong> geklont werden.
          </Form.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCloneModal(false)}>Abbrechen</Button>
          <Button variant="primary" disabled={!cloneTargetLayerId} onClick={handleCloneTC}>Klonen</Button>
        </Modal.Footer>
      </Modal>

      {/* ========= CUSTOM HOLIDAY MODAL ========= */}
      <Modal show={showCustomHolidayModal} onHide={() => setShowCustomHolidayModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Eigenen Feiertag hinzufügen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Name *</Form.Label>
            <Form.Control value={customHolidayForm.name} onChange={(e) => setCustomHolidayForm({ ...customHolidayForm, name: e.target.value })}
              placeholder="z.B. Firmenevent, Betriebsferien" />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Typ</Form.Label>
            <Form.Select value={customHolidayForm.holiday_type} onChange={(e) => setCustomHolidayForm({ ...customHolidayForm, holiday_type: e.target.value })}>
              <option value="FIXED">Festes Datum (jährlich)</option>
              <option value="EASTER_BASED">Relativ zu Ostern</option>
              <option value="CUSTOM">Bestimmtes Datum</option>
            </Form.Select>
          </Form.Group>
          {customHolidayForm.holiday_type === 'FIXED' && (
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Monat</Form.Label>
                  <Form.Select value={customHolidayForm.fixed_month} onChange={(e) => setCustomHolidayForm({ ...customHolidayForm, fixed_month: parseInt(e.target.value) })}>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <option key={m} value={m}>{['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'][m-1]}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Tag</Form.Label>
                  <Form.Control type="number" min={1} max={31} value={customHolidayForm.fixed_day} onChange={(e) => setCustomHolidayForm({ ...customHolidayForm, fixed_day: parseInt(e.target.value) || 1 })} />
                </Form.Group>
              </Col>
            </Row>
          )}
          {customHolidayForm.holiday_type === 'EASTER_BASED' && (
            <Form.Group className="mb-3">
              <Form.Label>Tage ab Ostersonntag</Form.Label>
              <Form.Control type="number" value={customHolidayForm.easter_offset}
                onChange={(e) => setCustomHolidayForm({ ...customHolidayForm, easter_offset: parseInt(e.target.value) || 0 })} />
              <Form.Text className="text-muted">z.B. -2 = Karfreitag, +1 = Ostermontag, +39 = Auffahrt</Form.Text>
            </Form.Group>
          )}
          {customHolidayForm.holiday_type === 'CUSTOM' && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Datum</Form.Label>
                <Form.Control type="date" value={customHolidayForm.custom_date}
                  onChange={(e) => setCustomHolidayForm({ ...customHolidayForm, custom_date: e.target.value })} />
              </Form.Group>
              <Form.Check type="switch" id="custom_recurs" label="Jährlich wiederholen (gleicher Tag/Monat)"
                checked={customHolidayForm.custom_recurs_yearly}
                onChange={(e) => setCustomHolidayForm({ ...customHolidayForm, custom_recurs_yearly: e.target.checked })} />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCustomHolidayModal(false)}>Abbrechen</Button>
          <Button variant="primary" disabled={!customHolidayForm.name} onClick={handleAddCustomHoliday}>Hinzufügen</Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
